import React from "react";
import { createPortal } from "react-dom";

import EcoLogoWhite from "../../../img/eco/logo-white.png";
import { DpRoster, minutesToTime, SCHEDULE_TYPE_LABELS, SHIFT_LABELS, StoreShiftCode, WEEKDAY_SHORT } from "../api/scheduleApi";
import "./RosterPrintSheet.css";

const LETTER: Record<string, string> = {
  MANHA: "M",
  NOITE: "N",
  FOLGA: "F",
  FERIADO: "FE",
  FERIAS: "FÉR",
  AFASTAMENTO: "AF",
  LOJA_FECHADA: "—",
};

const daysOf = (year: number, month: number) => {
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return Array.from({ length: last }, (_, i) => new Date(Date.UTC(year, month - 1, i + 1)));
};
const isoOf = (d: Date) => d.toISOString().slice(0, 10);

/**
 * Folha de impressão da escala com a identidade da Eco System. Fica no `body` (portal)
 * e só aparece em `@media print`, no lugar da página inteira — não é um print da tela.
 */
export default function RosterPrintSheet({ roster, issuer }: { roster: DpRoster; issuer?: string }) {
  const days = daysOf(roster.year, roster.month);
  const cells = new Map(roster.days.map((d) => [`${d.employeeId}|${d.date}`, d]));
  const monthLabel = new Date(Date.UTC(roster.year, roster.month - 1, 1)).toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  const activeShifts = roster.shifts.filter((s) => s.isActive);
  const count = (date: string, pred: (kind: string, shift: string | null) => boolean) =>
    roster.days.filter((d) => d.date === date && pred(d.kind, d.shift)).length;
  const now = new Date().toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

  const sheet = (
    <div className="rosterPrintRoot" aria-hidden="true">
      <header className="rpHeader">
        <img className="rpHeader__logo" src={EcoLogoWhite} alt="Eco System Serviços" />
        <div className="rpHeader__title">
          <span className="rpHeader__kicker">Escala mensal de trabalho</span>
          <strong className="rpHeader__store">{roster.storeName}</strong>
          <span className="rpHeader__month">{monthLabel}</span>
        </div>
        <div className="rpHeader__meta">
          <span className="rpChip">{SCHEDULE_TYPE_LABELS[roster.scheduleType]}</span>
          {roster.status === "PUBLICADO" ? (
            <span className="rpChip rpChip--ok">
              Publicada em {new Date(roster.publishedAt!).toLocaleDateString("pt-BR")}
            </span>
          ) : (
            <span className="rpChip rpChip--draft">Rascunho — sujeita a alteração</span>
          )}
        </div>
      </header>

      <section className="rpInfo">
        <div>
          <span className="rpInfo__label">Turnos</span>
          {activeShifts.map((s) => (
            <span key={s.code} className="rpInfo__value">
              <i className={`rpDot rpDot--${s.code}`} /> {SHIFT_LABELS[s.code]} {minutesToTime(s.startMinute)}–{minutesToTime(s.endMinute)}
            </span>
          ))}
        </div>
        <div>
          <span className="rpInfo__label">Colaboradores</span>
          <span className="rpInfo__value">{roster.employees.length}</span>
        </div>
        <div>
          <span className="rpInfo__label">Domingo de folga</span>
          <span className="rpInfo__value">ao menos 1 a cada {roster.sundayOffEveryWeeks} semanas</span>
        </div>
      </section>

      <table className="rpGrid">
        <thead>
          <tr>
            <th className="rpGrid__name">Colaborador</th>
            {days.map((d) => {
              const wd = d.getUTCDay();
              return (
                <th key={isoOf(d)} className={`${wd === 0 ? "is-sunday" : wd === 6 ? "is-saturday" : ""}${wd === 1 ? " is-weekStart" : ""}`}>
                  <span className="rpGrid__day">{d.getUTCDate()}</span>
                  <span className="rpGrid__wd">{WEEKDAY_SHORT[wd].slice(0, 3)}</span>
                </th>
              );
            })}
            <th className="rpGrid__total">Trab.</th>
            <th className="rpGrid__total">Folgas</th>
          </tr>
        </thead>
        <tbody>
          {roster.employees.map((e, i) => {
            let worked = 0;
            let rests = 0;
            return (
              <tr key={e.employeeId} className={i % 2 ? "is-odd" : ""}>
                <td className="rpGrid__name">
                  <strong>{e.name}</strong>
                  <small>{e.shift === "ROTATIVO" ? "Rotativo" : SHIFT_LABELS[e.shift as StoreShiftCode]}</small>
                </td>
                {days.map((d) => {
                  const c = cells.get(`${e.employeeId}|${isoOf(d)}`);
                  const weekStart = d.getUTCDay() === 1 ? " is-weekStart" : "";
                  if (!c) return <td key={isoOf(d)} className={`rpCell rpCell--empty${weekStart}`} />;
                  if (c.kind === "TRABALHO") worked += 1;
                  else if (c.kind === "FOLGA") rests += 1;
                  const variant = c.kind === "TRABALHO" ? c.shift ?? "MANHA" : c.kind;
                  return (
                    <td key={isoOf(d)} className={`rpCell rpCell--${variant}${weekStart}`}>
                      {c.kind === "TRABALHO" ? LETTER[c.shift ?? ""] ?? "?" : LETTER[c.kind]}
                    </td>
                  );
                })}
                <td className="rpGrid__total">{worked}</td>
                <td className="rpGrid__total">{rests}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          {activeShifts.map((s) => (
            <tr key={s.code}>
              <td className="rpGrid__name">
                <i className={`rpDot rpDot--${s.code}`} /> {SHIFT_LABELS[s.code]}
              </td>
              {days.map((d) => (
                <td key={isoOf(d)} className={d.getUTCDay() === 1 ? "is-weekStart" : ""}>
                  {count(isoOf(d), (k, sh) => k === "TRABALHO" && sh === s.code)}
                </td>
              ))}
              <td colSpan={2} />
            </tr>
          ))}
          <tr>
            <td className="rpGrid__name">
              <i className="rpDot rpDot--FOLGA" /> Folgas
            </td>
            {days.map((d) => (
              <td key={isoOf(d)} className={d.getUTCDay() === 1 ? "is-weekStart" : ""}>
                {count(isoOf(d), (k) => k === "FOLGA")}
              </td>
            ))}
            <td colSpan={2} />
          </tr>
        </tfoot>
      </table>

      <section className="rpLegend">
        <span><i className="rpSwatch rpCell--MANHA" /> M — Manhã</span>
        <span><i className="rpSwatch rpCell--NOITE" /> N — Noite</span>
        <span><i className="rpSwatch rpCell--FOLGA" /> F — Folga</span>
        <span><i className="rpSwatch rpCell--FERIAS" /> FÉR — Férias</span>
        <span><i className="rpSwatch rpCell--AFASTAMENTO" /> AF — Afastamento</span>
        <span><i className="rpSwatch rpCell--LOJA_FECHADA" /> — Loja fechada</span>
        <span className="rpLegend__week">Semanas de segunda a domingo (linha mais grossa = início da semana)</span>
      </section>

      <section className="rpSign">
        <div>
          <span className="rpSign__line" />
          <span>Departamento Pessoal</span>
        </div>
        <div>
          <span className="rpSign__line" />
          <span>Gerente da loja</span>
        </div>
        <div>
          <span className="rpSign__line" />
          <span>Data</span>
        </div>
      </section>

      <footer className="rpFooter">
        <span>
          <strong>Eco System Serviços</strong> · Escala sujeita às regras da CLT: máximo de 6 dias seguidos de trabalho e descanso semanal remunerado.
        </span>
        <span>
          Emitido em {now}
          {issuer ? ` por ${issuer}` : ""}
        </span>
      </footer>
    </div>
  );

  return createPortal(sheet, document.body);
}
